/** Internal messages: inbox, conversation, compose — with "response required" and supervisor escalation. */
import { useEffect, useState } from 'react';
import { RESPOND_DAYS } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, ErrorBox, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { ago, at, EmptyState, LINK_LABEL, linkTo, PeoplePicker, refreshMailCount, useMailCount, when } from './common.tsx';

type Box = 'inbox' | 'waiting' | 'sent' | 'archived';

function DueBadge({ m }: { m: any }) {
  if (m.kind === 'escalation')
    return m.about_responded_at ? (
      <Badge tone="ok">{m.about_name} answered</Badge>
    ) : (
      <Badge tone="bad" dot>
        Overdue — {m.about_name} hasn’t replied
      </Badge>
    );
  if (!m.response_required || m.kind === 'cc') return null;
  if (m.responded_at) return <Badge tone="ok">Replied</Badge>;
  if (m.respond_by == null) return null;
  const late = +new Date(m.respond_by) < Date.now();
  return (
    <Badge tone={late ? 'bad' : 'warn'} dot>
      {late ? 'Reply overdue' : `Reply by ${when(m.respond_by, { weekday: 'short', month: 'short', day: 'numeric' })}`}
    </Badge>
  );
}

export function MailPage() {
  const { navigate, path } = useRouter();
  const [box, setBox] = useState<Box>(() => (new URLSearchParams(path.split('?')[1] ?? '').get('box') as Box) || 'inbox');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const r = useLoad(() => api.get<any>(`/api/mail?box=${box}${query ? `&q=${encodeURIComponent(query)}` : ''}`), [box, query]);
  const c = useMailCount();
  const msgs: any[] = r.data?.messages ?? [];
  const tabs: [Box, string, number | null][] = [
    ['inbox', 'Inbox', c?.unread || null],
    ['waiting', 'Needs my reply', c?.needResponse || null],
    ['sent', 'Sent', null],
    ['archived', 'Archived', null],
  ];
  return (
    <div className="ops stack mail">
      <PageHeader
        crumb="Agency"
        title="Messages"
        sub="Messages inside CivilHound. Check “response required” and if they don’t answer in time, their supervisor is told."
        actions={
          <button className="btn primary" onClick={() => navigate(at('/messages/new'))}>
            New message
          </button>
        }
      />
      {c?.overdue ? (
        <div className="alert bad">
          You have {c.overdue} message{c.overdue === 1 ? '' : 's'} past the reply deadline. Your supervisor has been told — answer {c.overdue === 1 ? 'it' : 'them'} as soon as you can.
        </div>
      ) : null}
      <div className="filterbar">
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, message or sender" aria-label="Search messages" />
      </div>
      <div className="tabs" role="tablist">
        {tabs.map(([k, l, n]) => (
          <button key={k} role="tab" aria-selected={box === k} className={box === k ? 'active' : ''} onClick={() => setBox(k)}>
            {l} {n ? <span className="rp-n">{n}</span> : null}
          </button>
        ))}
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !msgs.length ? (
        <EmptyState icon="mail" title={query ? 'No messages match' : box === 'waiting' ? 'Nothing waiting on you' : box === 'sent' ? 'You haven’t sent anything yet' : 'No messages'} />
      ) : (
        <div className="card flush mail-list">
          {msgs.map((m) => (
            <button key={`${m.id}${m.kind ?? ''}`} type="button" className={`mail-row ${box !== 'sent' && !m.read_at ? 'unread' : ''} ${m.kind === 'escalation' ? 'esc' : ''}`} onClick={() => navigate(at(`/messages/${m.id}`))}>
              <span className="mr-from">
                {box === 'sent'
                  ? `To ${(m.recipients ?? []).filter((x: any) => x.kind === 'to').map((x: any) => x.name).join(', ')}`
                  : m.from_name}
              </span>
              <span className="mr-subj">
                <strong>{m.subject}</strong>
                <span className="muted"> — {m.snippet}</span>
              </span>
              <span className="mr-meta">
                {box === 'sent' && m.response_required ? (
                  <SentStatus m={m} />
                ) : (
                  <DueBadge m={m} />
                )}
                <span className="muted small">{ago(m.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SentStatus({ m }: { m: any }) {
  const to = (m.recipients ?? []).filter((x: any) => x.kind === 'to');
  const done = to.filter((x: any) => x.responded_at).length;
  const esc = to.filter((x: any) => x.escalated_at && !x.responded_at).length;
  return (
    <Badge tone={done === to.length ? 'ok' : esc ? 'bad' : 'warn'}>
      {done}/{to.length} replied{esc ? ` · ${esc} sent to supervisor` : ''}
    </Badge>
  );
}

export function MailThread({ id }: { id: string }) {
  const { me } = useAuth();
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/mail/${id}`), [id]);
  useEffect(() => {
    if (r.data) refreshMailCount();
  }, [r.data]);
  const [body, setBody] = useState('');
  const [all, setAll] = useState(false);
  const [req, setReq] = useState(false);
  const [days, setDays] = useState(3);
  const a = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const msgs: any[] = r.data.messages;
  const last = msgs[msgs.length - 1];
  const need = r.data.needsMyResponse as { id: string; respond_by: string }[];
  const multi = msgs.some((m) => (m.recipients ?? []).filter((x: any) => x.kind !== 'escalation').length > 1);
  const send = () =>
    a.run(async () => {
      await api.post(`/api/mail/${last.id}/reply`, { body, replyAll: all, responseRequired: req, respondDays: days });
      setBody('');
      setReq(false);
      r.reload();
      refreshMailCount();
    });
  return (
    <div className="ops stack mail">
      <PageHeader
        crumb={<Link to={at('/messages')}>Messages</Link>}
        title={msgs[0].subject}
        actions={
          <>
            <button className="btn" onClick={() => api.post(`/api/mail/${id}/${msgs[0].archived ? 'unarchive' : 'archive'}`).then(() => navigate(at('/messages')))}>
              {msgs[0].archived ? 'Move to inbox' : 'Archive'}
            </button>
            <button className="btn ghost" onClick={() => api.post(`/api/mail/${id}/unread`).then(() => (refreshMailCount(), navigate(at('/messages'))))}>
              Mark unread
            </button>
          </>
        }
      />
      {need.length > 0 && (
        <div className={`alert ${need.some((n) => +new Date(n.respond_by) < Date.now()) ? 'bad' : 'warn'}`}>
          <strong>A reply is required</strong> by {when(need[0]!.respond_by, { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.
          {need.some((n) => +new Date(n.respond_by) < Date.now()) ? ' It’s overdue and your supervisor has been told.' : ' If you don’t answer by then, your supervisor is told.'}
        </div>
      )}
      {msgs.map((m) => {
        const mine = m.from_user_id === me!.user.id;
        const rc = (m.recipients ?? []) as any[];
        const esc = rc.filter((x) => x.kind === 'escalation');
        return (
          <article key={m.id} className={`card mail-msg ${mine ? 'mine' : ''}`}>
            <header>
              <div>
                <strong>{m.from_name}</strong>
                {m.from_title && <span className="muted small"> · {m.from_title}</span>}
                <div className="muted small">
                  To {rc.filter((x) => x.kind === 'to').map((x) => x.name).join(', ')}
                  {rc.some((x) => x.kind === 'cc') && <> · CC {rc.filter((x) => x.kind === 'cc').map((x) => x.name).join(', ')}</>}
                </div>
              </div>
              <span className="muted small">{when(m.created_at, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
            </header>
            <div className="mail-body">{m.body}</div>
            {linkTo(m.link) && (
              <Link to={linkTo(m.link)!} className="btn sm mail-link">
                {LINK_LABEL[m.link.kind] ?? 'Open'} →
              </Link>
            )}
            {m.response_required && (
              <footer className="mail-status">
                <span className="small muted">Response required by {when(m.respond_by, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}:</span>
                {rc
                  .filter((x) => x.kind === 'to')
                  .map((x) => (
                    <Badge key={x.id} tone={x.responded_at ? 'ok' : x.escalated_at ? 'bad' : x.read_at ? 'info' : 'neutral'}>
                      {x.name}: {x.responded_at ? `replied ${ago(x.responded_at)}` : x.escalated_at ? 'overdue — supervisor told' : x.read_at ? 'read, no reply' : 'not read yet'}
                    </Badge>
                  ))}
                {esc.length > 0 && <span className="small muted">Sent to {esc.map((x) => `${x.name} (about ${x.about})`).join(', ')}</span>}
              </footer>
            )}
          </article>
        );
      })}
      <div className="card mail-reply">
        <label className="sr-only" htmlFor="reply">
          Reply
        </label>
        <textarea id="reply" rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a reply…" />
        <div className="row gap-sm wrap">
          {multi && (
            <label className="check">
              <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Reply to everyone
            </label>
          )}
          <RespondToggle on={req} setOn={setReq} days={days} setDays={setDays} />
          <span className="spacer" />
          <button className="btn primary" disabled={!body.trim() || a.busy} onClick={send}>
            Send reply
          </button>
        </div>
        <ErrorBox error={a.error} />
      </div>
    </div>
  );
}

function RespondToggle({ on, setOn, days, setDays }: { on: boolean; setOn: (b: boolean) => void; days: number; setDays: (n: number) => void }) {
  return (
    <span className="row gap-sm">
      <label className="check">
        <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} /> Response required
      </label>
      {on && (
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Respond within">
          {RESPOND_DAYS.map((d) => (
            <option key={d} value={d}>
              within {d} day{d === 1 ? '' : 's'}
            </option>
          ))}
        </select>
      )}
    </span>
  );
}

export function Compose() {
  const { navigate, path } = useRouter();
  const ppl = useLoad(() => api.get<any>('/api/mail/people'), []);
  const pre = new URLSearchParams(path.split('?')[1] ?? '').get('to');
  const [to, setTo] = useState<string[]>(pre ? [pre] : []);
  const [cc, setCc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [req, setReq] = useState(false);
  const [days, setDays] = useState(3);
  const a = useAction();
  const people = ppl.data?.people ?? [];
  return (
    <div className="ops stack mail">
      <PageHeader crumb={<Link to={at('/messages')}>Messages</Link>} title="New message" />
      <form
        className="card stack mail-compose"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            const m = await api.post<any>('/api/mail', { to, cc, subject, body, responseRequired: req, respondDays: days });
            navigate(at(`/messages/${m.id}`), true);
          });
        }}
      >
        <div className="mc-row">
          <span className="mc-k">To</span>
          <PeoplePicker people={people} value={to} onChange={setTo} label="To" />
          {!showCc && (
            <button type="button" className="btn link sm" onClick={() => setShowCc(true)}>
              CC
            </button>
          )}
        </div>
        {showCc && (
          <div className="mc-row">
            <span className="mc-k">CC</span>
            <PeoplePicker people={people} value={cc} onChange={setCc} label="CC" />
          </div>
        )}
        <div className="mc-row">
          <span className="mc-k">Subject</span>
          <input aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
        </div>
        <textarea aria-label="Message" rows={10} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your message…" />
        <div className="row gap-sm wrap">
          <RespondToggle on={req} setOn={setReq} days={days} setDays={setDays} />
          {req && <span className="small muted">Each person in “To” must reply in time, or their supervisor is told.</span>}
          <span className="spacer" />
          <button type="button" className="btn ghost" onClick={() => navigate(at('/messages'))}>
            Cancel
          </button>
          <button className="btn primary" disabled={!to.length || !subject.trim() || !body.trim() || a.busy}>
            Send
          </button>
        </div>
        <ErrorBox error={a.error} />
      </form>
    </div>
  );
}
